import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiHelpers } from '../services/api';
import { useAuthStore } from '../store/authStore';
import {
  ChartBarIcon,
  CircleStackIcon,
  ChatBubbleLeftRightIcon,
  UsersIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  MoonIcon,
  SunIcon,
  SparklesIcon,
  BuildingOffice2Icon,
  ChevronUpDownIcon,
  LinkIcon,
  UserCircleIcon,
  ServerStackIcon,
  QuestionMarkCircleIcon,
  BellIcon,
  CodeBracketIcon,
  GiftIcon
} from '@heroicons/react/24/outline';
import FoundersBadge from '../components/FoundersBadge';
import neoshiftLogo from '../images/neoshiftBILogo.png';
import TutorialManager from '../components/tutorial/TutorialManager';
import '../styles/tutorial.css';

// Analytics section
const analyticsNavigation = [
  { name: 'Dashboards', href: '/dashboards', icon: ChartBarIcon, tourId: 'nav-dashboards' },
  { name: 'AI Assistant', href: '/ai', icon: ChatBubbleLeftRightIcon, permission: 'use_ai', tourId: 'nav-ai-assistant' }
];

// Data Management section
const dataManagementNavigation = [
  { name: 'Data Studio', href: '/data/relationships', icon: SparklesIcon, primary: true, tourId: 'nav-data-studio' },
  { name: 'Data Sources', href: '/data', icon: CircleStackIcon, permission: 'view_data_sources', tourId: 'nav-data-sources' }
];

// Super Admin only navigation
const superAdminNavigation = [
  { name: 'Companies', href: '/admin/companies', icon: BuildingOffice2Icon }
];

const adminNavigation = [
  { name: 'Users', href: '/admin/users', icon: UsersIcon, permission: 'manage_company_users' },
  { name: 'Settings', href: '/admin/settings', icon: Cog6ToothIcon, permission: 'manage_own_company' },
  { name: 'Billing', href: '/admin/billing', icon: CreditCardIcon, permission: 'view_billing' },
  { name: 'Warehouse Storage', href: '/admin/warehouse', icon: ServerStackIcon, permission: 'view_billing' },
  { name: 'Referrals', href: '/referrals', icon: GiftIcon }
];

export default function MainLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [unreadTickets, setUnreadTickets] = useState(0);
  const { user, company, selectedCompany, logout, hasPermission, isSuperUser, getCurrentCompany, getCurrentCompanyId } = useAuthStore();
  const navigate = useNavigate();
  
  // Get current company ID for credit balance
  const currentCompanyId = getCurrentCompanyId();
  
  // Get current company for plan check
  const currentCompany = getCurrentCompany();
  const isEnterprise = currentCompany?.billingPlan === 'enterprise';
  const hasAdminAccess = user && ['SUPER_ADMIN', 'MANAGING_ADMIN', 'ADMIN'].includes(user.role);
  
  // Fetch SDK config to check if enabled (only for enterprise + admin users)
  const { data: sdkConfigData } = useQuery({
    queryKey: ['sdkConfig', currentCompanyId],
    queryFn: async () => {
      const result = await apiHelpers.getSDKConfig(currentCompanyId);
      return result.data;
    },
    enabled: !!currentCompanyId && isEnterprise && hasAdminAccess,
    staleTime: 60000, // Cache for 1 minute
  });
  
  const showSDKNav = isEnterprise && hasAdminAccess && sdkConfigData?.enabled;
  
  // Fetch credit balance
  const { data: creditBalance } = useQuery({
    queryKey: ['creditBalance', currentCompanyId],
    queryFn: async () => {
      console.log('[MainLayout] Fetching credit balance for company:', currentCompanyId);
      const result = await apiHelpers.getCreditBalance(currentCompanyId);
      console.log('[MainLayout] Credit balance received:', result.data);
      return result.data;
    },
    enabled: !!currentCompanyId,
    refetchInterval: 30000, // Refresh every 30 seconds
    staleTime: 0, // Always refetch when component mounts or window focuses
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchOnWindowFocus: true // Refetch when window regains focus
  });
  
  // Poll for unread ticket count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await apiHelpers.getUnreadTicketCount();
        setUnreadTickets(response.data.unreadCount || 0);
      } catch (error) {
        // Silently fail - not critical
      }
    };
    
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000); // Poll every minute
    
    return () => clearInterval(interval);
  }, []);
  
  const handleLogout = async () => {
    await logout();
    navigate('/');
  };
  
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };
  
  const filteredAdminNav = adminNavigation.filter(item => 
    !item.permission || hasPermission(item.permission)
  );
  
  const filteredAnalyticsNav = analyticsNavigation.filter(item => 
    !item.permission || hasPermission(item.permission)
  );
  
  const filteredDataManagementNav = dataManagementNavigation.filter(item => 
    !item.permission || hasPermission(item.permission)
  );
  
  const isSuper = isSuperUser();
  
  return (
    <div className="min-h-screen bg-surface-50 dark:bg-surface-950">
      {/* Tutorial Manager */}
      <TutorialManager />
      
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-white dark:bg-surface-900 
        border-r border-surface-200 dark:border-surface-800
        transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-surface-200 dark:border-surface-800">
            <NavLink to="/" className="flex items-center">
              <img 
                src={neoshiftLogo} 
                alt="Neoshift BI" 
                className="h-8 w-auto"
              />
            </NavLink>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          
          {/* Company badge / switcher */}
          {isSuper ? (
            <NavLink 
              to="/admin/companies"
              className="block px-4 py-3 border-b border-surface-200 dark:border-surface-800 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-surface-500 dark:text-surface-400 mb-1">
                    {selectedCompany ? 'Working as' : 'No company selected'}
                  </div>
                  <div className="font-medium text-surface-900 dark:text-white truncate">
                    {currentCompany?.name || 'Select a company'}
                  </div>
                </div>
                <ChevronUpDownIcon className="w-5 h-5 text-surface-400 flex-shrink-0" />
              </div>
            </NavLink>
          ) : company && (
            <div className="px-4 py-3 border-b border-surface-200 dark:border-surface-800">
              <div className="text-xs text-surface-500 dark:text-surface-400 mb-1">Company</div>
              <div className="font-medium text-surface-900 dark:text-white truncate">
                {company.name}
              </div>
            </div>
          )}
          
          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {/* Analytics section */}
            {filteredAnalyticsNav.length > 0 && (
              <>
                <div className="pb-2">
                  <div className="text-xs font-medium text-surface-400 dark:text-surface-500 uppercase tracking-wider px-3">
                    Analytics
                  </div>
                </div>
                <div className="space-y-1">
                  {filteredAnalyticsNav.map((item) => (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      className={({ isActive }) =>
                        isActive ? 'sidebar-link-active' : 'sidebar-link'
                      }
                      data-tour={item.tourId}
                    >
                      <item.icon className="w-5 h-5" />
                      {item.name}
                    </NavLink>
                  ))}
                </div>
              </>
            )}
            
            {/* Data Management section */}
            {filteredDataManagementNav.length > 0 && (
              <>
                <div className="pt-4 pb-2">
                  <div className="text-xs font-medium text-surface-400 dark:text-surface-500 uppercase tracking-wider px-3">
                    Data Management
                  </div>
                </div>
                <div className="space-y-1">
                  {filteredDataManagementNav.map((item) => (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      className={({ isActive }) =>
                        item.primary
                          ? isActive 
                            ? 'sidebar-link-active bg-gradient-to-r from-purple-500/10 to-pink-500/10' 
                            : 'sidebar-link hover:bg-gradient-to-r hover:from-purple-500/5 hover:to-pink-500/5'
                          : isActive ? 'sidebar-link-active' : 'sidebar-link'
                      }
                      data-tour={item.tourId}
                    >
                      <item.icon className={`w-5 h-5 ${item.primary ? 'text-purple-500' : ''}`} />
                      {item.name}
                    </NavLink>
                  ))}
                </div>
              </>
            )}
            
            {/* Super Admin nav */}
            {isSuper && (
              <>
                <div className="pt-4 pb-2">
                  <div className="text-xs font-medium text-surface-400 dark:text-surface-500 uppercase tracking-wider px-3">
                    Platform
                  </div>
                </div>
                <div className="space-y-1">
                  {superAdminNavigation.map((item) => (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      className={({ isActive }) =>
                        isActive ? 'sidebar-link-active' : 'sidebar-link'
                      }
                    >
                      <item.icon className="w-5 h-5" />
                      {item.name}
                    </NavLink>
                  ))}
                </div>
              </>
            )}
            
            {/* Admin nav */}
            {filteredAdminNav.length > 0 && (
              <>
                <div className="pt-4 pb-2">
                  <div className="text-xs font-medium text-surface-400 dark:text-surface-500 uppercase tracking-wider px-3">
                    Administration
                  </div>
                </div>
                <div className="space-y-1">
                  {filteredAdminNav.map((item) => (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      className={({ isActive }) =>
                        isActive ? 'sidebar-link-active' : 'sidebar-link'
                      }
                    >
                      <item.icon className="w-5 h-5" />
                      {item.name}
                    </NavLink>
                  ))}
                  
                  {/* SDK Integration - Only for Enterprise with SDK enabled */}
                  {showSDKNav && (
                    <NavLink
                      to="/admin/sdk"
                      className={({ isActive }) =>
                        isActive 
                          ? 'sidebar-link-active bg-gradient-to-r from-emerald-500/10 to-teal-500/10' 
                          : 'sidebar-link hover:bg-gradient-to-r hover:from-emerald-500/5 hover:to-teal-500/5'
                      }
                    >
                      <CodeBracketIcon className="w-5 h-5 text-emerald-500" />
                      SDK Integration
                    </NavLink>
                  )}
                </div>
              </>
            )}
          </nav>
          
          {/* Help & User section */}
          <div className="p-4 border-t border-surface-200 dark:border-surface-800">
            {/* Help Button */}
            <NavLink
              to="/help"
              className={({ isActive }) =>
                `flex items-center gap-3 mb-3 p-2 -mx-2 rounded-lg transition-colors ${
                  isActive 
                    ? 'bg-surface-100 dark:bg-surface-800 text-surface-900 dark:text-white' 
                    : 'text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800 hover:text-surface-900 dark:hover:text-white'
                }`
              }
            >
              <QuestionMarkCircleIcon className="w-5 h-5" />
              <span className="font-medium">Help</span>
            </NavLink>
            
            <NavLink 
              to="/settings"
              className="flex items-center gap-3 mb-3 p-2 -mx-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
            >
              <div className="relative">
                <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center text-white font-medium">
                  {user?.firstName?.[0]}{user?.lastName?.[0]}
                </div>
                {/* Founders badge indicator on avatar */}
                {user?.hasFoundersBadge && (
                  <span className="absolute -bottom-1 -right-1 text-xs" title="Beta Founder">🏆</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-surface-900 dark:text-white truncate">
                    {user?.firstName} {user?.lastName}
                  </span>
                  {user?.hasFoundersBadge && (
                    <FoundersBadge size="small" showTooltip={false} />
                  )}
                </div>
                <div className="text-sm text-surface-500 dark:text-surface-400 truncate flex items-center gap-1">
                  <UserCircleIcon className="w-3.5 h-3.5" />
                  Account Settings
                </div>
              </div>
            </NavLink>
            
            <div className="flex items-center gap-2">
              <button
                onClick={toggleDarkMode}
                className="flex-1 btn-ghost text-sm"
              >
                {darkMode ? (
                  <SunIcon className="w-4 h-4 mr-2" />
                ) : (
                  <MoonIcon className="w-4 h-4 mr-2" />
                )}
                {darkMode ? 'Light' : 'Dark'}
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 btn-ghost text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
              >
                <ArrowRightOnRectangleIcon className="w-4 h-4 mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </aside>
      
      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-16 bg-white/80 dark:bg-surface-900/80 backdrop-blur-xl border-b border-surface-200 dark:border-surface-800">
          <div className="flex items-center justify-between h-full px-4 lg:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800"
            >
              <Bars3Icon className="w-6 h-6 text-surface-600 dark:text-surface-400" />
            </button>
            
            <div className="flex-1" />
            
            <div className="flex items-center gap-2">
              {/* Credit Balance Display - Only show if company is selected */}
              {currentCompanyId && creditBalance?.hasCompany && (
                <button
                  onClick={() => navigate('/admin/billing?action=buy-credits')}
                  className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors cursor-pointer group"
                  title="Click to purchase credits"
                >
                  <SparklesIcon className="w-5 h-5 text-primary-500 group-hover:text-primary-600" />
                  <div className="flex flex-col items-start">
                    <span className="text-xs text-surface-500 dark:text-surface-400 leading-none">Credits</span>
                    <span className="text-sm font-semibold text-surface-900 dark:text-white leading-none mt-0.5">
                      {creditBalance?.balance?.totalAvailable?.toLocaleString() || 0}
                    </span>
                  </div>
                </button>
              )}
              
              {/* Notification Bell */}
              <button
                onClick={() => navigate('/tickets')}
                className="relative p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
                title="Support Tickets"
              >
                <BellIcon className="w-6 h-6 text-surface-600 dark:text-surface-400" />
                {unreadTickets > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {unreadTickets > 9 ? '9+' : unreadTickets}
                  </span>
                )}
              </button>
            </div>
          </div>
        </header>
        
        {/* Page content */}
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

